import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Search, PlayCircle, RotateCcw, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Clause, ProjectStatus } from '../types';
import { POLLING_INTERVALS } from '../constants';

// 清理重复编号和内容的工具函数
const cleanDuplicatedNumbering = (text: string): string => {
  if (!text) return text;
  
  let cleaned = text;
  
  // 1. 处理 "第一条第一条" 这样的重复
  cleaned = cleaned.replace(/^(第[一二三四五六七八九十百]+条)\1/, '$1');
  cleaned = cleaned.replace(/^(第\d+条)\1/, '$1');
  
  // 2. 查找文本中所有的小数编号
  const allNums = cleaned.match(/\d+\.\d+/g) || [];
  
  if (allNums.length >= 2) {
    // 查找后面出现的编号，检查文本开头是否是该编号的重复
    for (let i = 1; i < allNums.length; i++) {
      const num = allNums[i];
      const doubled = num + num;
      if (cleaned.startsWith(doubled)) {
        // 找到了！文本开头是重复的编号
        cleaned = num + ' ' + cleaned.substring(doubled.length).trimStart();
        break;
      }
    }
  }
  
  // 3. 检查内容重复
  const numberingMatch = cleaned.match(/^(\d+\.\d+)/);
  
  if (numberingMatch) {
    const numbering = numberingMatch[1];
    const escapedNum = numbering.replace(/\./g, '\\.');
    
    // 查找编号在文本中的所有出现位置
    const pattern = new RegExp(`${escapedNum}\\s+`, 'g');
    const matches: { index: number; length: number }[] = [];
    let match;
    while ((match = pattern.exec(cleaned)) !== null) {
      matches.push({ index: match.index, length: match[0].length });
    }
    
    if (matches.length >= 2) {
      // 有第二次出现的编号
      const secondMatch = matches[1];
      const firstPart = cleaned.substring(0, secondMatch.index).trim();
      const secondPart = cleaned.substring(secondMatch.index + secondMatch.length).trim();
      
      // 从第一部分提取内容（编号之后的部分）
      const firstContentMatch = firstPart.match(new RegExp(`^${escapedNum}\\s+(.+)$`, 's'));
      if (firstContentMatch) {
        const firstContent = firstContentMatch[1].trim();
        // 如果第二部分与第一部分内容相似，则是重复
        const minLen = Math.min(firstContent.length, secondPart.length);
        if (minLen > 5) {
          const checkLen = Math.min(20, minLen);
          if (firstContent.substring(0, checkLen) === secondPart.substring(0, checkLen)) {
            return firstPart;
          }
        }
      }
    }
  }
  
  // 4. 处理多行文本中的重复行
  const lines = cleaned.split('\n');
  if (lines.length > 1) {
    const uniqueLines: string[] = [];
    const seen = new Set<string>();
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed);
        uniqueLines.push(line);
      }
    }
    cleaned = uniqueLines.join('\n');
  }
  
  return cleaned;
};

// 清理 HTML 标签和实体的工具函数（前端兜底）
const cleanHtmlTags = (text: string): string => {
  if (!text) return text;
  
  let cleaned = text;
  
  // 1. 剥离所有 HTML 标签，保留内部文本
  cleaned = cleaned.replace(/<[^>]+>/g, ' ');
  
  // 2. 清理常见 HTML 实体
  const entities: Record<string, string> = {
    '&nbsp;': ' ',
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&mdash;': '—',
    '&ndash;': '–',
    '&hellip;': '…',
  };
  for (const [entity, char] of Object.entries(entities)) {
    cleaned = cleaned.split(entity).join(char);
  }
  
  // 3. 清理数字型 HTML 实体
  cleaned = cleaned.replace(/&#(\d+);/g, (_, code) => {
    const num = parseInt(code, 10);
    return num < 65536 ? String.fromCharCode(num) : ' ';
  });
  
  // 4. 移除残留的 HTML 属性片段
  cleaned = cleaned.replace(/\b(?:colspan|rowspan|cellpadding|cellspacing|border|width|height|align|valign|style|class|id)\s*=\s*"[^"]*"/g, '');
  
  // 5. 合并多余空白
  cleaned = cleaned.replace(/[ \t]+/g, ' ');
  cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
  
  // 6. 清理每行首尾空白
  cleaned = cleaned.split('\n').map(l => l.trim()).filter(l => l).join('\n');
  
  return cleaned.trim();
};

const compareClauses = (a: Clause, b: Clause): number => {
  return (a.sequence || 0) - (b.sequence || 0);
};

const SplitPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const { 
    currentProject, 
    clauses, 
    loadProject, 
    loadClauses, 
    startSplit, 
    pollSplitStatus,
  } = useApp();
  
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [splitting, setSplitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load project and check status
  useEffect(() => {
    if (projectId && projectId !== 'new') {
      loadProject(projectId).then(() => {
        setLoading(false);
      }).catch(() => {
        // 如果加载失败，重定向到上传页面
        navigate(`/project/${projectId}/upload`);
      });
    } else if (projectId === 'new') {
      // 新项目需要先上传文件
      navigate('/project/new/upload');
    }
  }, [projectId]);

  // Handle project status changes
  useEffect(() => {
    if (!currentProject) return;
    
    if (currentProject.status === ProjectStatus.PARSING) {
      setSplitting(true);
      startPolling();
    } else if (currentProject.status === ProjectStatus.PARSED || 
               currentProject.status === ProjectStatus.REVIEWED ||
               currentProject.status === ProjectStatus.COMPLETED) {
      setSplitting(false);
      setLoading(false);
      if (clauses.length === 0) {
        loadClauses(currentProject.id);
      }
    } else if (currentProject.status === ProjectStatus.FILES_UPLOADED ||
               currentProject.status === ProjectStatus.CREATED) {
      // Need to start split
      setLoading(false);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [currentProject?.status]);

  const startPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    pollingRef.current = setInterval(async () => {
      if (!projectId) return;
      
      const completed = await pollSplitStatus(projectId);
      if (completed) {
        setSplitting(false);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      }
    }, POLLING_INTERVALS.SPLIT_STATUS);
  };

  const handleStartSplit = async () => {
    if (!projectId || projectId === 'new') return;
    
    setSplitting(true);
    setLoading(true);
    
    try {
      await startSplit(projectId);
      startPolling();
    } catch (e) {
      setSplitting(false);
      setLoading(false);
    }
  };

  const handleReSplit = async () => {
    if (!projectId || projectId === 'new') return;
    
    setSplitting(true);
    
    try {
      await startSplit(projectId);
      startPolling();
    } catch (e) {
      setSplitting(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Filter clauses by search
  const orderedClauses = [...clauses].sort(compareClauses);

  const filteredClauses = orderedClauses.filter(clause => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      clause.content.toLowerCase().includes(query) ||
      clause.title?.toLowerCase().includes(query) ||
      clause.clause_no?.toLowerCase().includes(query)
    );
  });

  // Show loading state
  if (loading || splitting) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-white">
        <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mb-4"></div>
        <h2 className="text-xl font-bold text-slate-800">
          {splitting ? '正在 AI 分析合同...' : '正在加载...'}
        </h2>
        <p className="text-slate-500">
          {splitting ? 'AI 正在分离正文与附加协议，并逐条分解条款' : '请稍候'}
        </p>
      </div>
    );
  }

  // Show start split button if not yet split
  if (clauses.length === 0 && currentProject?.status !== ProjectStatus.PARSED) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-white">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-slate-800 mb-2">准备 AI 分析合同</h2>
          <p className="text-slate-500 mb-6">
            AI 将自动分离合同正文与附加协议，并将正文逐条分解为独立条款（保留原文）。
          </p>
          <button
            onClick={handleStartSplit}
            className="flex items-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 shadow-lg mx-auto"
          >
            <PlayCircle size={20} />
            开始 AI 分析
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen max-h-screen overflow-hidden">
      {/* Header */}
      <div className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 z-20">
        <div>
          <h1 className="text-lg font-bold text-slate-800">AI 条款分解</h1>
          <p className="text-xs text-slate-500">
            共 {orderedClauses.filter(c => c.clause_type === 'main_body').length} 个正文条款
            {clauses.some(c => c.clause_type === 'supplementary') && (
              <span className="ml-2 text-amber-600">+ 附加协议</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleReSplit}
            disabled={splitting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors disabled:opacity-50"
          >
            <RotateCcw size={16} />
            重新 AI 分析
          </button>
          <button 
            onClick={() => navigate(`/project/${projectId}/review`)}
            className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md shadow-md hover:shadow-lg transition-all"
          >
            <PlayCircle size={16} />
            开始 AI 审查
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="h-12 bg-slate-50 border-b border-slate-200 px-6 flex items-center gap-4 shrink-0">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input 
            type="text" 
            placeholder="搜索条款内容..." 
            className="w-full pl-9 pr-4 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="h-6 w-px bg-slate-300 mx-2"></div>
        <span className="text-sm text-slate-500">
          显示 {filteredClauses.length} / {orderedClauses.length} 条款
        </span>
      </div>

      {/* Split View Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar (TOC) */}
        <div className="w-80 bg-white border-r border-slate-200 overflow-y-auto hidden lg:block">
          <div className="p-4">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">目录</h3>
            <ul className="space-y-1">
              {filteredClauses.map(clause => (
                <li key={clause.id}>
                  <button 
                    onClick={() => {
                      const el = document.getElementById(`clause-${clause.id}`);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="w-full text-left block px-3 py-2 text-sm text-slate-600 rounded-md hover:bg-slate-50 hover:text-slate-900 truncate"
                  >
                    <span className="font-semibold mr-2">{cleanDuplicatedNumbering(clause.clause_no || '') || clause.sequence}</span>
                    {cleanDuplicatedNumbering(clause.title || '') || '(无标题)'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-slate-50/50 p-6 scroll-smooth">
          <div className="max-w-4xl mx-auto space-y-4">
            {filteredClauses.map((clause) => {
              const isExpanded = expanded[clause.id] !== false; // Default expanded
              const cleanedClauseNo = cleanHtmlTags(cleanDuplicatedNumbering(clause.clause_no || ''));
              const cleanedTitle = cleanHtmlTags(cleanDuplicatedNumbering(clause.title || ''));
              const cleanedContent = cleanHtmlTags(cleanDuplicatedNumbering(clause.content));
              const isSupplementary = clause.clause_type === 'supplementary';
              
              return (
                <div 
                  key={clause.id} 
                  id={`clause-${clause.id}`} 
                  className={`bg-white border rounded-lg shadow-sm overflow-hidden transition-all hover:shadow-md ${
                    isSupplementary ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'
                  }`}
                >
                  <div 
                    className={`px-4 py-3 border-b flex items-center justify-between cursor-pointer select-none ${
                      isSupplementary ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'
                    }`}
                    onClick={() => toggleExpand(clause.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400">
                        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </span>
                      <span className="font-mono font-bold text-slate-700">
                        {cleanedClauseNo || `#${clause.sequence}`}
                      </span>
                      {cleanedTitle && (
                        <span className="font-semibold text-slate-900">{cleanedTitle}</span>
                      )}
                      {isSupplementary && (
                        <span className="px-2 py-0.5 rounded text-xs bg-amber-200 text-amber-800 font-medium">
                          附加协议
                        </span>
                      )}
                      {!isSupplementary && clause.party_tags && clause.party_tags.length > 0 && (
                        <span className="px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-600 font-medium">
                          {clause.party_tags.join(', ')}
                        </span>
                      )}
                    </div>
                    {clause.source_page && clause.source_page.length > 0 && (
                      <span className="text-xs text-slate-400">
                        页 {clause.source_page.join(', ')}
                      </span>
                    )}
                  </div>
                  
                  {isExpanded && (
                    <div className="p-5 text-slate-800 text-base leading-relaxed whitespace-pre-wrap font-serif">
                      {cleanedContent}
                    </div>
                  )}
                </div>
              );
            })}
            
            {filteredClauses.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
                <p>没有找到匹配的条款</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitPage;
